import React from "react";
import DatasourceCardLayout from "../../../shared/cards/DatasourceCardLayout";
import AccordionLayout, { accordionExpandedCacheKey } from "../../../shared/accordions/AccordionLayout";
import { workbookCache } from "../../../../excel/workbook/cache";
import { useProject } from "../../../context/ProjectContext";
import { useDatasources } from "../../../context/DatasourceContext";
import { useBanner } from "../../../context/BannerContext";
import { EDIPHI_DATASOURCE_META } from "../meta";
import {
  LAST_REFRESHED,
  LAST_REFRESHED_FORMAT,
  COST_MODEL_REMOVED_DATA_REMAINS,
  EDIPHI_DATASOURCE_DELETE_NOTICE,
} from "../../../constants";
import { costModelLabel } from "../../../../utils/format";
import { formatCurrency, formatCompactCurrency, formatValue } from "../format";
import { openCostModelInEdiphi } from "../../../../utils/links";
import { useCostModelLoader } from "../../../hooks/useCostModelLoader";
import { useCardSummary } from "../../../hooks/useCardSummary";

interface CostModelDatasourceCardProps {
  onCardClick: () => void;
}

const DETAILS_CACHE_KEY = accordionExpandedCacheKey("cost-model");

const CostModelDatasourceCard = ({ onCardClick }: CostModelDatasourceCardProps) => {
  const { selectedCostModel, setSelectedCostModel } = useProject();
  const { removeDatasource } = useDatasources();
  const { showBanner } = useBanner();
  const { loadCostModel, clearCostModel } = useCostModelLoader();
  const summary = useCardSummary("cost-model");

  if (!selectedCostModel) return null;

  const label = summary?.label ?? costModelLabel(selectedCostModel);
  const metricValue = (value: number | null | undefined) => (value == null ? "—" : formatCurrency(value));

  const handleRemoveCard = async () => {
    removeDatasource("ediphi", "cost-model");
    setSelectedCostModel(null);
    void workbookCache.delete(DETAILS_CACHE_KEY);

    // The React state above is already cleared, so a failure here leaves the card gone but the
    // workbook still holding this cost model's data — say so rather than failing silently.
    try {
      await clearCostModel();
    } catch {
      showBanner(COST_MODEL_REMOVED_DATA_REMAINS, "error");
    }
  };

  return (
    <DatasourceCardLayout
      header={{
        icon: EDIPHI_DATASOURCE_META["cost-model"].icon,
        text: EDIPHI_DATASOURCE_META["cost-model"].label,
      }}
      card={{
        text: label,
        subtext: summary ? formatCompactCurrency(summary.grandTotal) : "—",
      }}
      onCardClick={onCardClick}
      onLaunch={() => openCostModelInEdiphi(selectedCostModel.project, selectedCostModel.id)}
      onRefresh={() => void loadCostModel(selectedCostModel)}
      onClose={handleRemoveCard}
      deleteNotice={EDIPHI_DATASOURCE_DELETE_NOTICE}
    >
      <AccordionLayout
        details={[
          { label: "Grand Total", value: metricValue(summary?.grandTotal) },
          { label: "Gross Area", value: summary ? formatValue(summary.grossArea, 0, "", "SF") : "—" },
          { label: "Cost per SF", value: summary ? formatValue(summary.costPerSqft, 2, "$", "") : "—" },
        ]}
        lastUpdated={summary?.lastRefreshedAt}
        lastUpdatedLabel={LAST_REFRESHED}
        lastUpdatedFormat={LAST_REFRESHED_FORMAT}
        cacheKey={DETAILS_CACHE_KEY}
      />
    </DatasourceCardLayout>
  );
};

export default CostModelDatasourceCard;
